import React, { useState, useEffect } from 'react';
import { galleryApi } from '../../services/gallery';
import { useAuth } from '../../context/AuthContext';

export const CommentsSection = ({ photoId, photoData, onPhotoUpdate }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const [suggestedDate, setSuggestedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  // Загрузка комментариев при монтировании и смене фото
  useEffect(() => {
    loadComments();
  }, [photoId]);

  const loadComments = async () => {
    try {
      const data = await galleryApi.getComments(photoId);
      setComments(data.comments || []);
    } catch (err) {
      console.error('Ошибка загрузки комментариев:', err);
    }
  };

  // Отправка нового комментария
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setLoading(true);
    try {
      await galleryApi.createComment(
        photoId,
        newComment,
        suggestedTitle,
        suggestedDate,
        user.token
      );
      setNewComment('');
      setSuggestedTitle('');
      setSuggestedDate('');
      await loadComments(); // Перезагружаем, чтобы увидеть свой комментарий
    } catch (err) {
      alert('Ошибка: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Начало редактирования
  const handleEdit = (comment) => {
    setEditingId(comment.pk_comment);
    setEditText(comment.description);
  };

  // Сохранение изменений
  const handleSaveEdit = async (commentId) => {
    if (!editText.trim()) {
      alert('Комментарий не может быть пустым');
      return;
    }
    try {
      await galleryApi.updateComment(commentId, editText, user.token);
      setEditingId(null);
      setEditText('');
      await loadComments();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  // Удаление комментария
  const handleDelete = async (commentId) => {
    if (!window.confirm('Удалить комментарий?')) return;
    try {
      await galleryApi.deleteComment(commentId, user.token);
      await loadComments();
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  // Модерация (только для админов)
  const handleModerate = async (commentId, approved) => {
    if (!user?.is_admin) return;
    try {
      await galleryApi.moderateComment(commentId, approved, user.token);
      await loadComments();
      if (approved && onPhotoUpdate) {
        onPhotoUpdate();
      }
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  // Фильтрация: показываем одобренные + свои (даже на модерации)
  const visibleComments = comments.filter(c => {
    if (user?.is_admin) return true; // Админ видит все
    if (c.approved === 1) return true; // Одобренные видны всем
    if (user && c.email === user.email) return true; // Свои видны автору
    return false;
  });

  // Счётчик одобренных (для заголовка)
  const approvedCount = comments.filter(c => c.approved === 1).length;

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>
        💬 Комментарии ({approvedCount})
      </h3>

      {/* Список комментариев */}
      <div style={styles.commentsList}>
        {visibleComments.length === 0 ? (
          <p style={styles.empty}>Пока нет комментариев</p>
        ) : (
          visibleComments.map(comment => {
            const isMyComment = user && comment.email === user.email;
            const isPending = comment.approved === 0;

            return (
              <div
                key={comment.pk_comment}
                style={{
                  ...styles.comment,
                  opacity: isPending ? 0.7 : 1,
                  borderLeft: isPending ? '3px solid #f39c12' : '3px solid var(--accent)',
                  background: isPending ? 'rgba(243, 156, 18, 0.05)' : 'rgba(0,0,0,0.2)'
                }}
              >
                <div style={styles.commentHeader}>
                  <strong style={styles.author}>{comment.username}</strong>
                  <span style={styles.date}>
                    {new Date(comment.date_comment).toLocaleString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                  {isPending && (
                    <span style={styles.badge}>⏳ На модерации</span>
                  )}
                </div>

                {editingId === comment.pk_comment ? (
                  <div>
                    <textarea
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      style={styles.textarea}
                      rows={3}
                    />
                    <div style={styles.actions}>
                      <button onClick={() => handleSaveEdit(comment.pk_comment)} style={styles.btnSmall}>
                        💾 Сохранить
                      </button>
                      <button onClick={() => { setEditingId(null); setEditText(''); }} style={styles.btnSmall}>
                        Отмена
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={styles.text}>{comment.description}</p>
                )}

                {/* Кнопки действий */}
                <div style={styles.commentActions}>
                  {/* Владелец может редактировать/удалять свои комментарии в ЛЮБОМ статусе */}
                  {isMyComment && editingId !== comment.pk_comment && (
                    <>
                      <button onClick={() => handleEdit(comment)} style={styles.btnAction} title="Редактировать">
                        ✏️
                      </button>
                      <button onClick={() => handleDelete(comment.pk_comment)} style={styles.btnAction} title="Удалить">
                        🗑
                      </button>
                    </>
                  )}

                  {/* 🔥 Админ может удалять ЛЮБЫЕ комментарии */}
                  {user?.is_admin && !isMyComment && (
                    <button
                      onClick={() => handleDelete(comment.pk_comment)}
                      style={{ ...styles.btnAction, color: '#e74c3c' }}
                      title="Удалить комментарий (админ)"
                    >
                      🗑 Удалить
                    </button>
                  )}

                  {/* Админ управляет модерацией */}
                  {user?.is_admin && isPending && (
                    <>
                      <button
                        onClick={() => handleModerate(comment.pk_comment, true)}
                        style={{ ...styles.btnAction, color: '#27ae60' }}
                        title="Одобрить"
                      >
                        ✅ Одобрить
                      </button>
                      <button
                        onClick={() => handleModerate(comment.pk_comment, false)}
                        style={{ ...styles.btnAction, color: '#e74c3c' }}
                        title="Отклонить"
                      >
                        ❌ Отклонить
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Форма добавления комментария */}
      {user ? (
        <form onSubmit={handleSubmit} style={styles.form}>
          <textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Напишите комментарий..."
            style={styles.textarea}
            rows={3}
          />

          {/* Поля для предложения изменений */}
          <div style={styles.suggestions}>
            <input
              type="text"
              value={suggestedTitle}
              onChange={e => setSuggestedTitle(e.target.value)}
              placeholder="Предложить новое название (необязательно)"
              style={styles.input}
            />
            <input
              type="date"
              value={suggestedDate}
              onChange={e => setSuggestedDate(e.target.value)}
              placeholder="Предложить новую дату"
              style={styles.input}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !newComment.trim()}
            style={{
              ...styles.btnSubmit,
              opacity: loading || !newComment.trim() ? 0.5 : 1,
              cursor: loading || !newComment.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Отправка...' : 'Отправить комментарий'}
          </button>
        </form>
      ) : (
        <p style={styles.loginPrompt}>
          🔒 <a href="#auth" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Войдите</a>, чтобы оставить комментарий
        </p>
      )}
    </div>
  );
};

const styles = {
  container: {
    marginTop: '60px',
    padding: '30px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '12px',
    border: '1px solid rgba(201,169,110,0.15)'
  },
  title: {
    fontSize: '1.3rem',
    marginBottom: '25px',
    color: 'var(--accent)',
    fontFamily: 'Cormorant Garamond, serif',
    fontWeight: 400
  },
  commentsList: {
    marginBottom: '30px',
    maxHeight: '500px',
    overflowY: 'auto'
  },
  comment: {
    padding: '20px',
    marginBottom: '15px',
    borderRadius: '8px',
    transition: 'all 0.2s'
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
    flexWrap: 'wrap'
  },
  author: {
    color: 'var(--accent)',
    fontSize: '0.95rem',
    fontFamily: 'Special Elite, monospace'
  },
  date: {
    color: 'var(--text-muted)',
    fontSize: '0.85rem'
  },
  badge: {
    background: 'rgba(243,156,18,0.2)',
    color: '#f39c12',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    marginLeft: 'auto'
  },
  text: {
  color: 'var(--text-light)',
  lineHeight: 1.6,
  margin: '10px 0',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',     
  overflowWrap: 'break-word',    
  maxWidth: '100%',              
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.05rem'
},
  commentActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '10px',
    flexWrap: 'wrap'
  },
  btnAction: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '5px 8px',
    opacity: 0.7,
    transition: 'opacity 0.2s',
    color: 'var(--text-muted)'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: 'var(--text-light)',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontSize: '1rem'
  },
  suggestions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  input: {
    flex: '1',
    minWidth: '200px',
    padding: '10px',
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: 'var(--text-light)',
    fontSize: '0.9rem',
    boxSizing: 'border-box'
  },
  btnSubmit: {
    background: 'var(--accent)',
    border: 'none',
    color: 'var(--bg-dark)',
    padding: '12px 24px',
    borderRadius: '8px',
    fontFamily: 'Special Elite, monospace',
    fontWeight: 600,
    alignSelf: 'flex-start',
    transition: 'opacity 0.2s'
  },
  btnSmall: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    color: 'var(--text-light)',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    marginRight: '8px',
    transition: 'background 0.2s'
  },
  actions: {
    marginTop: '10px'
  },
  empty: {
    color: 'var(--text-muted)',
    textAlign: 'center',
    padding: '30px',
    fontStyle: 'italic'
  },
  loginPrompt: {
    color: 'var(--text-muted)',
    textAlign: 'center',
    padding: '20px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '8px'
  }
};